class MegaMenuBestSellers {
  constructor() {
    this.menus = document.querySelectorAll('[data-mega-menu]');
    this.cache = new Map();
    this.init();
  }

  init() {
    this.menus.forEach((menu) => {
      const triggers = menu.querySelectorAll('[data-mega-menu-trigger]');
      const productsContainer = menu.querySelector('[data-mega-menu-products]');
      const loadingEl = menu.querySelector('[data-mega-menu-loading]');
      const ctaContainer = menu.querySelector('[data-mega-menu-cta-container]');
      const ctaButton = ctaContainer?.querySelector('a, button');

      triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => {
          this.handleHover(
            trigger,
            productsContainer,
            loadingEl,
            ctaButton,
            triggers
          );
        });
      });
    });
  }

  async handleHover(trigger, container, loadingEl, ctaButton, allTriggers) {
    const handle = trigger.dataset.collectionHandle;
    if (!handle) return;

    allTriggers.forEach((t) => t.classList.remove('is-active'));
    trigger.classList.add('is-active');

    if (ctaButton) {
      ctaButton.href = trigger.href;
      ctaButton.textContent = 'Shop All';
    }

    if (this.cache.has(handle)) {
      this.renderProducts(container, this.cache.get(handle));
      return;
    }

    container.style.opacity = '0.5';
    if (loadingEl) loadingEl.hidden = false;

    try {
      const featuredHandles = trigger.dataset.featuredHandles
        ? trigger.dataset.featuredHandles.split(',').filter(Boolean)
        : [];

      const products = await this.fetchProducts(handle, featuredHandles);
      this.cache.set(handle, products);
      this.renderProducts(container, products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      container.style.opacity = '1';
      if (loadingEl) loadingEl.hidden = true;
    }
  }

  async fetchProducts(handle, featuredHandles = []) {
    const bestSellersPromise = fetch(
      `/collections/${handle}/products.json?sort_by=best-selling&limit=3`
    ).then((r) => r.json());

    const featuredPromise = featuredHandles.length > 0
      ? Promise.all(
          featuredHandles.slice(0, 3).map((h) =>
            fetch(`/products/${h}.json`)
              .then((r) => r.json())
              .then((d) => d.product)
              .catch(() => null)
          )
        )
      : Promise.resolve([]);

    const [bestSellersData, featuredProducts] = await Promise.all([
      bestSellersPromise,
      featuredPromise
    ]);

    const featured = featuredProducts.filter(Boolean);
    const featuredIds = new Set(featured.map((p) => p.id));

    const fillers = (bestSellersData.products || [])
      .filter((p) => !featuredIds.has(p.id))
      .slice(0, 3 - featured.length);

    return [...featured, ...fillers];
  }

 renderProducts(container, products) {
  if (!products || products.length === 0) {
    container.innerHTML = `<p class="mega-menu__empty">No products found</p>`;
    return;
  }

  const rootUrl = (window.GroundedRoutes?.rootUrl ?? '').replace(/\/+$/, '');

  const placeholderSvg = `
    <svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525">
      <rect fill="#f8f8f8" width="525" height="525"/>
      <path fill="#d9d9d9" d="M324.5 212.7H203.2c-3.1 0-5.6 2.5-5.6 5.6v92.3c0 3.1 2.5 5.6 5.6 5.6h121.3c3.1 0 5.6-2.5 5.6-5.6v-92.3c0-3.1-2.5-5.6-5.6-5.6zm-60.7 85.3c-16.4 0-29.8-13.4-29.8-29.8s13.4-29.8 29.8-29.8 29.8 13.4 29.8 29.8-13.4 29.8-29.8 29.8z"/>
    </svg>
  `;

  const productSlots = [0, 1, 2]
    .map((index) => {
      const product = products[index];
      if (product) {
        const imageUrl = this.getProductImage(product);
        const truncatedTitle =
          product.title.length > 25
            ? product.title.substring(0, 25) + '...'
            : product.title;

        return `
          <a href="${rootUrl}/products/${product.handle}" class="mega-menu__product">
            <div class="mega-menu__product-media">
              ${
                imageUrl
                  ? `<img
                      src="${imageUrl}"
                      alt="${product.title}"
                      loading="lazy"
                      class="mega-menu__product-img"
                    />`
                  : placeholderSvg
              }
            </div>
            <span class="mega-menu__product-title">${truncatedTitle}</span>
          </a>
        `;
      }
      return `<div class="mega-menu__product mega-menu__product--empty"></div>`;
    })
    .join('');

  container.innerHTML = productSlots;

  container.querySelectorAll('.mega-menu__product-img').forEach((img) => {
    if (img.complete) {
      checkImageBackground(img);
    } else {
      img.addEventListener('load', () => checkImageBackground(img));
    }
  });
}

  getProductImage(product) {
    let imageUrl = null;

    if (product.featured_image && typeof product.featured_image === 'string') {
      imageUrl = product.featured_image;
    } else if (product.featured_image && product.featured_image.src) {
      imageUrl = product.featured_image.src;
    } else if (product.images && product.images.length > 0) {
      imageUrl =
        typeof product.images[0] === 'string'
          ? product.images[0]
          : product.images[0].src;
    }

    if (!imageUrl) return null;

    return this.resizeImage(imageUrl, 200);
  }

  resizeImage(url, size) {
    if (!url) return '';

    let cleanUrl = url.replace(/_\d+x\d*\./, '.');
    const [baseUrl, queryString] = cleanUrl.split('?');
    const resizedUrl = baseUrl.replace(/(\.[^.]+)$/, `_${size}x$1`);

    return queryString ? `${resizedUrl}?${queryString}` : resizedUrl;
  }
}

function checkImageBackground(img) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;

  try {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const samples = 20;
    const samplePoints = [];

    for (let i = 0; i < samples; i++) {
      samplePoints.push({ x: Math.floor((canvas.width / samples) * i), y: 0 });
      samplePoints.push({ x: Math.floor((canvas.width / samples) * i), y: canvas.height - 1 });
    }

    for (let i = 0; i < samples; i++) {
      samplePoints.push({ x: 0, y: Math.floor((canvas.height / samples) * i) });
      samplePoints.push({ x: canvas.width - 1, y: Math.floor((canvas.height / samples) * i) });
    }

    let whitePixelCount = 0;

    samplePoints.forEach((point) => {
      const pixelData = ctx.getImageData(point.x, point.y, 1, 1).data;
      const [r, g, b, a] = pixelData;
      if (r > 240 && g > 240 && b > 240 && a > 200) {
        whitePixelCount++;
      }
    });

    if (whitePixelCount / samplePoints.length > 0.7) {
      img.style.border = '1px solid rgb(235, 235, 235)';
    }
  } catch (e) {
    console.warn('Could not analyze image:', e);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MegaMenuBestSellers());
} else {
  new MegaMenuBestSellers();
}